import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Mail, Phone, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { EmptyState, PageHeader } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { ClientForm } from "../components/SimpleForms";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="animate-fade-up" data-testid="clients-page">
      <PageHeader overline="Kundenverwaltung" title="Kunden" testid="clients-header">
        <Button data-testid="new-client-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
          <Plus className="mr-1.5 h-4 w-4" /> Neuer Kunde
        </Button>
      </PageHeader>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">{[...Array(3)].map((_, i) => <div key={i} className="h-44 animate-pulse rounded-2xl bg-white" />)}</div>
      ) : clients.length === 0 ? (
        <EmptyState icon={Users} title="Noch keine Kunden" hint="Lege deinen ersten Kunden an – danach kannst du Projekte erstellen." testid="clients-empty">
          <Button data-testid="empty-new-client-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
            <Plus className="mr-1.5 h-4 w-4" /> Kunden anlegen
          </Button>
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {clients.map((c) => (
            <button
              key={c.id}
              data-testid={`client-card-${c.id}`}
              onClick={() => navigate(`/kunden/${c.id}`)}
              className="rounded-2xl border border-[#1D225C]/10 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#B88A4A]/40 hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-display text-lg font-bold text-[#1D225C]">{c.company}</p>
                  {c.contact_person && <p className="text-sm text-[#777]">{c.contact_person}</p>}
                </div>
                <span className="rounded-full bg-[#1D225C]/5 px-2.5 py-1 text-xs font-semibold text-[#1D225C]">
                  {c.project_count} Projekt{c.project_count === 1 ? "" : "e"}
                </span>
              </div>
              <div className="mt-3 space-y-1.5 text-xs text-[#777]">
                {c.email && <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-[#B88A4A]" /> {c.email}</p>}
                {c.phone && <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-[#B88A4A]" /> {c.phone}</p>}
                {c.website && <p className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-[#B88A4A]" /> {c.website}</p>}
              </div>
              {c.active_projects > 0 && (
                <p className="mt-3 inline-block rounded-full bg-[#B88A4A]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#B88A4A]">
                  {c.active_projects} aktiv
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      <ClientForm open={formOpen} onOpenChange={setFormOpen} client={editing} onSaved={load} />
    </div>
  );
}
